/*
 * Grid heatmap on a single-hue sequential ramp (never a rainbow). Used for the
 * hour × weekday usage grid and for retention cohorts.
 */

import { useState } from 'react';

const RAMP = ['var(--seq-1)', 'var(--seq-2)', 'var(--seq-3)', 'var(--seq-4)', 'var(--seq-5)', 'var(--seq-6)', 'var(--seq-7)'];

export interface HeatCell {
  row: number;
  col: number;
  /** null renders as an empty slot — "no data yet", not zero. */
  value: number | null;
}

interface Props {
  cells: HeatCell[];
  rowLabels: string[];
  colLabels: string[];
  /** Only every Nth column label is drawn, so a 24-hour axis does not collide. */
  colLabelEvery?: number;
  formatValue: (v: number) => string;
  formatTip: (row: number, col: number, v: number) => string;
  min?: number;
  max?: number;
  cellHeight?: number;
  legendLabel?: string;
}

export function Heatmap({
  cells,
  rowLabels,
  colLabels,
  colLabelEvery = 1,
  formatValue,
  formatTip,
  min,
  max,
  cellHeight = 26,
  legendLabel,
}: Props) {
  const [hover, setHover] = useState<HeatCell | null>(null);

  const values = cells.map((c) => c.value).filter((v): v is number => v !== null);
  const lo = min ?? Math.min(...values);
  const hi = max ?? Math.max(...values);
  const span = hi - lo || 1;

  const stepFor = (v: number) => {
    const t = (v - lo) / span;
    return RAMP[Math.min(RAMP.length - 1, Math.max(0, Math.round(t * (RAMP.length - 1))))];
  };

  const lookup = new Map(cells.map((c) => [`${c.row}:${c.col}`, c]));

  return (
    <div className="chart-wrap">
      <div className="heat" style={{ ['--cell-h' as string]: `${cellHeight}px` }}>
        <div
          className="heat__grid"
          style={{ gridTemplateColumns: `auto repeat(${colLabels.length}, minmax(0, 1fr))` }}
        >
          <div />
          {colLabels.map((c, i) => (
            <div className="heat__collabel" key={c}>
              {i % colLabelEvery === 0 ? c : ''}
            </div>
          ))}

          {rowLabels.map((rl, r) => (
            <div className="heat__rowgroup" key={rl} style={{ display: 'contents' }}>
              <div className="heat__rowlabel">{rl}</div>
              {colLabels.map((_, c) => {
                const cell = lookup.get(`${r}:${c}`);
                const v = cell?.value ?? null;
                const isHover = hover?.row === r && hover?.col === c;
                return (
                  <div
                    key={c}
                    className={`heat__cell${v === null ? ' heat__cell--empty' : ''}${isHover ? ' is-hover' : ''}`}
                    style={v === null ? undefined : { background: stepFor(v) }}
                    onMouseEnter={() => cell && v !== null && setHover(cell)}
                    onMouseLeave={() => setHover(null)}
                    tabIndex={v === null ? -1 : 0}
                    onFocus={() => cell && v !== null && setHover(cell)}
                    onBlur={() => setHover(null)}
                    role="img"
                    aria-label={v === null ? 'no data' : formatTip(r, c, v)}
                  >
                    {v !== null && cellHeight >= 30 ? (
                      <span className="heat__val">{formatValue(v)}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="heat__foot">
          <span className="heat__tip">
            {hover && hover.value !== null ? formatTip(hover.row, hover.col, hover.value) : legendLabel}
          </span>
          <span className="heat__scale">
            <span className="heat__scale-label">{formatValue(lo)}</span>
            {RAMP.map((step) => (
              <span key={step} className="heat__swatch" style={{ background: step }} />
            ))}
            <span className="heat__scale-label">{formatValue(hi)}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
